/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { absolutizeDependencies } from './absolutizeDependencies';
import { CellError, simpleCellAddress } from './Cell';
import { CellContent } from './CellContentParser';
import { getRawValue } from './interpreter/InterpreterValue';
import { FormulaCellVertex, MatrixVertex, ParsingErrorVertex, ValueCellVertex } from './DependencyGraph';
import { GraphBuilderMatrixHeuristic } from './GraphBuilderMatrixHeuristic';
import { checkMatrixSize } from './Matrix';
import { StatType } from './statistics';
/**
 * Service building the graph and mappings.
 */
export class GraphBuilder {
    /**
     * Configures the building service.
     *
     * @param graph - graph instance in which we want to add vertices and edges
     * @param addressMapping - mapping from addresses to vertices
     * @param rangeMapping - mapping from ranges to range vertices
     * @param stats - dependency tracking building performance
     * @param config - configuration of the sheet
     */
    constructor(dependencyGraph, columnSearch, parser, cellContentParser, config, stats) {
        this.dependencyGraph = dependencyGraph;
        this.columnSearch = columnSearch;
        this.parser = parser;
        this.cellContentParser = cellContentParser;
        this.config = config;
        this.stats = stats;
        if (this.config.matrixDetection) {
            this.buildStrategy = new MatrixDetectionStrategy(this.dependencyGraph, this.columnSearch, this.parser, this.stats, config.matrixDetectionThreshold, this.cellContentParser);
        }
        else {
            this.buildStrategy = new SimpleStrategy(this.dependencyGraph, this.columnSearch, this.parser, this.stats, this.cellContentParser);
        }
    }
    /**
     * Builds graph.
     *
     * @param sheet - two-dimensional array representation of sheet
     */
    buildGraph(sheets) {
        const dependencies = this.buildStrategy.run(sheets);
        this.processDependencies(dependencies);
    }
    processDependencies(dependencies) {
        dependencies.forEach((cellDependencies, endVertex) => {
            this.dependencyGraph.processCellDependencies(cellDependencies, endVertex);
        });
    }
}
export class SimpleStrategy {
    constructor(dependencyGraph, columnIndex, parser, stats, cellContentParser) {
        this.dependencyGraph = dependencyGraph;
        this.columnIndex = columnIndex;
        this.parser = parser;
        this.stats = stats;
        this.cellContentParser = cellContentParser;
    }
    run(sheets) {
        const dependencies = new Map();
        for (const sheetName in sheets) {
            const sheetId = this.dependencyGraph.getSheetId(sheetName);
            const sheet = sheets[sheetName];
            for (let i = 0; i < sheet.length; ++i) {
                const row = sheet[i];
                for (let j = 0; j < row.length; ++j) {
                    const cellContent = row[j];
                    const address = simpleCellAddress(sheetId, j, i);
                    const parsedCellContent = this.cellContentParser.parse(cellContent);
                    if (parsedCellContent instanceof CellContent.MatrixFormula) {
                        if (this.dependencyGraph.existsVertex(address)) {
                            continue;
                        }
                        const parseResult = this.stats.measure(StatType.PARSER, () => this.parser.parse(parsedCellContent.formula, address));
                        if (parseResult.errors.length > 0) {
                            const vertex = new ParsingErrorVertex(parseResult.errors, parsedCellContent.formulaWithBraces());
                            this.dependencyGraph.addVertex(address, vertex);
                        }
                        else {
                            const vertex = buildMatrixVertex(parseResult.ast, address);
                            dependencies.set(vertex, absolutizeDependencies(parseResult.dependencies, address));
                            this.dependencyGraph.addMatrixVertex(address, vertex);
                        }
                    }
                    else if (parsedCellContent instanceof CellContent.Formula) {
                        const parseResult = this.stats.measure(StatType.PARSER, () => this.parser.parse(parsedCellContent.formula, address));
                        if (parseResult.errors.length > 0) {
                            const vertex = new ParsingErrorVertex(parseResult.errors, parsedCellContent.formula);
                            this.dependencyGraph.addVertex(address, vertex);
                        }
                        else {
                            const vertex = new FormulaCellVertex(parseResult.ast, address, 0);
                            dependencies.set(vertex, absolutizeDependencies(parseResult.dependencies, address));
                            this.dependencyGraph.addVertex(address, vertex);
                            if (parseResult.hasVolatileFunction) {
                                this.dependencyGraph.markAsVolatile(vertex);
                            }
                            if (parseResult.hasStructuralChangeFunction) {
                                this.dependencyGraph.markAsDependentOnStructureChange(vertex);
                            }
                        }
                    }
                    else if (parsedCellContent instanceof CellContent.Empty) {
                        /* we don't care about empty cells here */
                    }
                    else {
                        const vertex = new ValueCellVertex(parsedCellContent.value, cellContent);
                        this.columnIndex.add(getRawValue(parsedCellContent.value), address);
                        this.dependencyGraph.addVertex(address, vertex);
                    }
                }
            }
        }
        return dependencies;
    }
}
export class MatrixDetectionStrategy {
    constructor(dependencyGraph, columnSearch, parser, stats, threshold, cellContentParser) {
        this.dependencyGraph = dependencyGraph;
        this.columnSearch = columnSearch;
        this.parser = parser;
        this.stats = stats;
        this.threshold = threshold;
        this.cellContentParser = cellContentParser;
    }
    run(sheets) {
        const dependencies = new Map();
        const matrixHeuristic = new GraphBuilderMatrixHeuristic(this.dependencyGraph, this.columnSearch, this.threshold, this.cellContentParser);
        for (const sheetName in sheets) {
            const sheetId = this.dependencyGraph.getSheetId(sheetName);
            const sheet = sheets[sheetName];
            matrixHeuristic.addSheet(sheetId, {
                width: this.dependencyGraph.getSheetWidth(sheetId),
                height: this.dependencyGraph.getSheetHeight(sheetId),
            });
            for (let i = 0; i < sheet.length; ++i) {
                const row = sheet[i];
                for (let j = 0; j < row.length; ++j) {
                    const cellContent = row[j];
                    const address = simpleCellAddress(sheetId, j, i);
                    const parsedCellContent = this.cellContentParser.parse(cellContent);
                    if (parsedCellContent instanceof CellContent.MatrixFormula) {
                        if (this.dependencyGraph.existsVertex(address)) {
                            continue;
                        }
                        const parseResult = this.stats.measure(StatType.PARSER, () => this.parser.parse(parsedCellContent.formula, address));
                        if (parseResult.errors.length > 0) {
                            const vertex = new ParsingErrorVertex(parseResult.errors, parsedCellContent.formulaWithBraces());
                            this.dependencyGraph.addVertex(address, vertex);
                        }
                        else {
                            const vertex = buildMatrixVertex(parseResult.ast, address);
                            dependencies.set(vertex, absolutizeDependencies(parseResult.dependencies, address));
                            this.dependencyGraph.addMatrixVertex(address, vertex);
                        }
                    }
                    else if (parsedCellContent instanceof CellContent.Formula) {
                        const parseResult = this.stats.measure(StatType.PARSER, () => this.parser.parse(parsedCellContent.formula, address));
                        if (parseResult.errors.length > 0) {
                            const vertex = new ParsingErrorVertex(parseResult.errors, parsedCellContent.formula);
                            this.dependencyGraph.addVertex(address, vertex);
                        }
                        else {
                            const vertex = new FormulaCellVertex(parseResult.ast, address, 0);
                            dependencies.set(vertex, absolutizeDependencies(parseResult.dependencies, address));
                            this.dependencyGraph.addVertex(address, vertex);
                        }
                    }
                    else if (parsedCellContent instanceof CellContent.Empty) {
                        /* we don't care about empty cells here */
                    }
                    else if (parsedCellContent instanceof CellContent.Number) {
                        matrixHeuristic.add(address);
                    }
                    else {
                        const vertex = new ValueCellVertex(parsedCellContent.value, cellContent);
                        this.columnSearch.add(parsedCellContent.value, address);
                        this.dependencyGraph.addVertex(address, vertex);
                    }
                }
            }
        }
        this.stats.start(StatType.MATRIX_DETECTION);
        const notMatrices = matrixHeuristic.run(sheets);
        for (let i = notMatrices.length - 1; i >= 0; --i) {
            const elem = notMatrices[i];
            elem.cells.reverse();
            for (const address of elem.cells) {
                const value = sheets[this.dependencyGraph.getSheetName(address.sheet)][address.row][address.col];
                const vertex = new ValueCellVertex(Number(value), value);
                this.columnSearch.add(Number(value), address);
                this.dependencyGraph.addVertex(address, vertex);
            }
        }
        this.stats.end(StatType.MATRIX_DETECTION);
        return dependencies;
    }
}
export function buildMatrixVertex(ast, formulaAddress) {
    const size = checkMatrixSize(ast, formulaAddress);
    if (size instanceof CellError) {
        return new ValueCellVertex(size, undefined);
    }
    return new MatrixVertex(formulaAddress, size.width, size.height, ast);
}
